// The embeddable "Pay with ConfirmPay" / "Contribute with ConfirmPay" button: one source of truth
// shared by the /embed/<slug> function, the dashboard's Embed tab (which previews that page and
// writes the copy-paste code) and the tests. Import-free so plain Node can load it.
//
// The rendered page is plain HTML and CSS with NO JavaScript, since many hosts drop <script>
// embeds; the button is just a link. Not every builder allows even an iframe (JotForm did not work).

export type EmbedKind = "pay" | "contribute";
export type EmbedTheme = "dark" | "light";
/** What the button shows: live (a link), paused (public link switched off)
 *  or missing (no such link). */
export type EmbedView = "live" | "paused" | "missing";

export const EMBED_LABEL: Record<EmbedKind, string> = {
  pay: "Pay with ConfirmPay",
  contribute: "Contribute with ConfirmPay",
};
const LEAD: Record<EmbedKind, string> = {
  pay: "Pay with",
  contribute: "Contribute with",
};
const PAUSED_TEXT: Record<EmbedKind, string> = {
  pay: "Payments paused",
  contribute: "Contributions paused",
};

/** The button's measurements and colours, read by both the CSS below and the image generator
 *  (scripts/build-embed-buttons.mjs) so the live button and its picture can't drift apart. */
export const BUTTON_STYLE = {
  padX: 18,
  gap: 10,
  radius: 6,
  border: 1.5,
  fontSize: 16,
  logoHeight: 22,
  ink: "#0e1007",
  paper: "#f2f0ef",
} as const;

export function buttonLead(kind: EmbedKind): string {
  return LEAD[kind];
}

/** Fixed, so the iframe can be sized exactly without JavaScript to measure
 *  the content. Wide enough for the label and logo, with a little slack. */
const BUTTON_WIDTH: Record<EmbedKind, number> = { pay: 240, contribute: 292 };
export const BUTTON_HEIGHT = 48;
/** Room around the button for the keyboard-focus ring. */
export const FRAME_PAD = 6;

export function buttonSize(kind: EmbedKind): { width: number; height: number } {
  return { width: BUTTON_WIDTH[kind], height: BUTTON_HEIGHT };
}

/** The width and height of the <iframe> that holds the button. */
export function frameSize(kind: EmbedKind): { width: number; height: number } {
  return {
    width: BUTTON_WIDTH[kind] + 2 * FRAME_PAD,
    height: BUTTON_HEIGHT + 2 * FRAME_PAD,
  };
}

/** Where the static image version of each button lives (see
 *  scripts/build-embed-buttons.mjs). */
export function buttonImagePath(kind: EmbedKind, theme: EmbedTheme): string {
  return `/brand/button-${kind}-${theme}.png`;
}

export function parseKind(v: string | null | undefined): EmbedKind {
  return v === "contribute" ? "contribute" : "pay";
}

export function parseTheme(v: string | null | undefined): EmbedTheme {
  return v === "light" ? "light" : "dark";
}

/** A prefilled amount from a link: plain digits with up to two decimals, more
 *  than zero. Anything else is ignored rather than fixed up. */
export function cleanAmount(v: string | null | undefined): string | null {
  const s = (v ?? "").trim();
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(s)) return null;
  return Number(s) > 0 ? s : null;
}

/** A prefilled "what it's for": control characters out, whitespace
 *  collapsed, capped at the 140 characters the payment form allows. */
export function cleanPurpose(v: string | null | undefined): string {
  let out = "";
  for (const ch of v ?? "") {
    const c = ch.codePointAt(0)!;
    out += c < 0x20 || c === 0x7f ? " " : ch;
  }
  return Array.from(out.replace(/\s+/g, " ").trim()).slice(0, 140).join("");
}

export interface Prefill {
  amount: string | null;
  purpose: string;
}

/** Where a click goes: the org's own payment page, marked as coming from a
 *  button (the paid screen then tells the payer they can close the tab). */
export function payerLinkPath(slug: string, p: Prefill): string {
  const q = ["from=button"];
  if (p.amount) q.push(`amount=${encodeURIComponent(p.amount)}`);
  if (p.purpose) q.push(`for=${encodeURIComponent(p.purpose)}`);
  return `/${slug}?${q.join("&")}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Brand values (see src/styles.css): ink #0e1007, paper #f2f0ef,
// sage-ink #40501a for the focus ring.
const CSS = `
@font-face{font-family:"Schibsted Grotesk";src:url(/brand/schibsted-grotesk.woff2) format("woff2");font-weight:400 900;font-display:swap}
html,body{margin:0;background:transparent;overflow:hidden}
body{padding:${FRAME_PAD}px;font-family:"Schibsted Grotesk","Helvetica Neue",Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;-webkit-tap-highlight-color:transparent}
.b{box-sizing:border-box;display:flex;align-items:center;justify-content:center;gap:${BUTTON_STYLE.gap}px;height:${BUTTON_HEIGHT}px;padding:0 ${BUTTON_STYLE.padX}px;border-radius:${BUTTON_STYLE.radius}px;border:${BUTTON_STYLE.border}px solid ${BUTTON_STYLE.ink};font-size:${BUTTON_STYLE.fontSize}px;font-weight:600;line-height:1;white-space:nowrap;text-decoration:none;cursor:pointer;transition:background-color .12s ease,transform .12s ease}
.b img{display:block;height:${BUTTON_STYLE.logoHeight}px;width:auto}
.dark{background:${BUTTON_STYLE.ink};color:${BUTTON_STYLE.paper}}
.dark:hover{background:#272a14}
.dark:active{background:#1a1c0a;transform:translateY(1px)}
.light{background:${BUTTON_STYLE.paper};color:${BUTTON_STYLE.ink}}
.light:hover{background:#e4e1da}
.light:active{background:#d9d6cf;transform:translateY(1px)}
.b:focus-visible{outline:0;box-shadow:0 0 0 3px #f2f0ef,0 0 0 6px #40501a}
.off{gap:8px;cursor:not-allowed;border-style:dashed}
.off.dark{background:#2b2d1e;border-color:#6a6c5a;color:#a4a698}
.off.light{background:#e8e5e0;border-color:#8f8c82;color:#55574d}
@media (prefers-reduced-motion:reduce){.b{transition:none}}
`.trim();

const PAUSE_ICON =
  '<svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true"><rect x="3" y="2" width="2.6" height="10" rx=".6"/><rect x="8.4" y="2" width="2.6" height="10" rx=".6"/></svg>';

/**
 * The whole HTML document that goes inside the iframe. `href` is where a
 * live button leads (a path on this site); paused and missing buttons are
 * not links at all.
 */
export function renderEmbedPage(o: {
  kind: EmbedKind;
  theme: EmbedTheme;
  view: EmbedView;
  href?: string;
}): string {
  const { kind, theme, view } = o;
  const width = BUTTON_WIDTH[kind];
  const style = `width:${width}px`;

  let button: string;
  if (view === "live" && o.href) {
    const logo = theme === "dark" ? "/brand/logo-dark.png" : "/brand/logo-light.png";
    button =
      `<a class="b ${theme}" style="${style}" href="${escapeHtml(o.href)}" target="_blank" rel="noopener">` +
      `<span>${LEAD[kind]}</span>` +
      `<img src="${logo}" alt="ConfirmPay" width="123" height="22">` +
      `</a>`;
  } else {
    const text = view === "missing" ? "Link not found" : PAUSED_TEXT[kind];
    button =
      `<span class="b off ${theme}" style="${style}" aria-disabled="true">${PAUSE_ICON}<span>${text}</span></span>`;
  }

  return (
    `<!doctype html>\n<html lang="en">\n<head>\n<meta charset="utf-8">\n` +
    `<meta name="viewport" content="width=device-width,initial-scale=1">\n` +
    `<meta name="robots" content="noindex">\n` +
    `<title>${EMBED_LABEL[kind]}</title>\n<style>${CSS}</style>\n</head>\n` +
    `<body>${button}</body>\n</html>\n`
  );
}

/** The query string of an iframe's src. */
export function embedQuery(o: { kind: EmbedKind; theme: EmbedTheme } & Prefill): string {
  const q = [`button=${o.kind}`, `theme=${o.theme}`];
  if (o.amount) q.push(`amount=${encodeURIComponent(o.amount)}`);
  if (o.purpose) q.push(`for=${encodeURIComponent(o.purpose)}`);
  return q.join("&");
}

export interface EmbedCode {
  /** For a website or a form builder with an HTML or embed block. */
  iframe: string;
  /** For email, documents and READMEs: an image that links to the page. */
  imageHtml: string;
  /** The same pieces on their own, for places that take them separately. */
  imageUrl: string;
  linkUrl: string;
}

/** A URL placed inside an HTML attribute. */
function attr(url: string): string {
  return escapeHtml(url);
}

export function buildEmbedCode(
  origin: string,
  slug: string,
  o: { kind: EmbedKind; theme: EmbedTheme } & Prefill,
): EmbedCode {
  const { width, height } = frameSize(o.kind);
  const label = EMBED_LABEL[o.kind];
  const src = `${origin}/embed/${slug}?${embedQuery(o)}`;
  const imageUrl = `${origin}${buttonImagePath(o.kind, o.theme)}`;
  const linkUrl = `${origin}${payerLinkPath(slug, o)}`;
  const img = buttonSize(o.kind);

  const iframe =
    `<iframe\n  src="${attr(src)}"\n  title="${label}"\n  width="${width}" height="${height}"\n` +
    `  style="border:0;color-scheme:light"></iframe>`;
  const imageHtml =
    `<a href="${attr(linkUrl)}">\n  <img src="${attr(imageUrl)}"\n    alt="${label}"\n` +
    `    width="${img.width}" height="${img.height}">\n</a>`;

  return { iframe, imageHtml, imageUrl, linkUrl };
}
